import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AssistantService, Assistant, LobbyAssistant } from '../../services/assistant.service';
import { FileUploadService } from '../../services/file-upload.service';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { environment } from '../../services/global-environment';


@Component({
  selector: 'app-ai-assistant-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslatePipe],
  templateUrl: './ai-assistant-editor.component.html',
  styleUrl: './ai-assistant-editor.component.scss'
})
export class AiAssistantEditorComponent implements OnInit {
  assistantForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  companyid = environment.companyid;
  assistantNo = '';

  // 助手下拉選單相關
  assistantOptions: LobbyAssistant[] = [];
  isLoadingAssistants = false;

  // 圖片相關
  selectedFile: File | null = null;
  uploadProgress = 0;
  isUploading = false;
  imagePreviewUrl = '';

  // 預設顏色選項
  presetColors: any[] = [];

  // 預設文字顏色選項
  presetFontColors: any[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private assistantService: AssistantService,
    private fileUploadService: FileUploadService,
    private i18nService: I18nService,
  ) {
    this.assistantForm = this.createAssistantForm();
  }

  /**
   * 取得翻譯服務的便捷方法
   */
  private get translate() {
    return this.i18nService;
  }

  ngOnInit(): void {
    this.initializeComponent();
    this.loadAssistantOptions();
    this.initializePresetColors();
  }

  /**
   * 初始化預設顏色
   */
  private initializePresetColors(): void {
    this.presetColors = [
      { name: this.translate.translate('assistant.editor.preset_color_ci_main'), value: '#f5820d' },
      { name: this.translate.translate('assistant.editor.preset_color_ci_secondary'), value: '#2e9116' },
      { name: this.translate.translate('assistant.editor.preset_color_dark_gray'), value: '#484949' },
      { name: this.translate.translate('assistant.editor.preset_color_blue'), value: '#007bff' },
      { name: this.translate.translate('assistant.editor.preset_color_red'), value: '#dc3545' },
      { name: this.translate.translate('assistant.editor.preset_color_purple'), value: '#6f42c1' },
      { name: this.translate.translate('assistant.editor.preset_color_cyan'), value: '#20c997' },
      { name: this.translate.translate('assistant.editor.preset_color_yellow'), value: '#ffc107' }
    ];

    this.presetFontColors = [
      { name: this.translate.translate('assistant.editor.preset_color_white'), value: '#ffffff' },
      { name: this.translate.translate('assistant.editor.preset_color_black'), value: '#000000' }
    ];
  }

  /**
   * 初始化元件
   */
  private initializeComponent(): void {
    // 不再自動從 URL 參數設定 assistantNo，讓使用者從下拉選單選擇
    // this.assistantNo = this.route.snapshot.queryParams['id'] || '';

    // 保持 assistantNo 為空，強制使用者選擇
    this.assistantNo = '';

    // 不自動載入助手資訊，等待使用者選擇
    // if (this.assistantNo) {
    //   this.loadAssistantInfo();
    // }
  }

  /**
   * 載入助手選項
   */
  private loadAssistantOptions(): void {
    this.isLoadingAssistants = true;

    this.assistantService.getCustomerConfig().subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.lobby) {
          this.assistantOptions = response.data.lobby;
        } else {
          console.warn(this.translate.translate('assistant.editor.console_warn_load_options'));
        }
        this.isLoadingAssistants = false;
      },
      error: (error) => {
        console.error(this.translate.translate('assistant.editor.console_error_load_options'), error);
        this.isLoadingAssistants = false;
      }
    });
  }

  /**
   * 助手選擇變更處理
   */
  onAssistantChange(selectedAssistantNo: string): void {
    if (selectedAssistantNo) {
      this.assistantNo = selectedAssistantNo;
      this.clearMessages();
      this.loadAssistantInfo();
    } else {
      // 選擇空值時清空表單和助手編號
      this.assistantNo = '';
      this.assistantForm.reset();
      this.imagePreviewUrl = '';
      this.clearMessages();
      // 重置預設顏色值
      this.assistantForm.patchValue({
        assistant_bg_color: '#f5820d',
        assistant_font_color: '#ffffff'
      });
    }
  }

  /**
   * 清除訊息
   */
  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  /**
   * 創建助手表單
   */
  private createAssistantForm(): FormGroup {
    return this.fb.group({
      title: ['', [Validators.maxLength(100)]],
      content: ['', [Validators.maxLength(2000)]],
      imageurl: [''],
      assistant_text: ['', [Validators.maxLength(50)]],
      assistant_bg_color: ['#f5820d'], // 預設使用 CI 主色作為背景色
      assistant_font_color: ['#ffffff'], // 預設使用白色作為文字顏色
      memo: ['', [Validators.maxLength(500)]]
    });
  }

  /**
   * 載入助手資訊
   */
  private loadAssistantInfo(): void {
    if (!this.assistantNo) {
      this.errorMessage = this.translate.translate('assistant.editor.error_select_assistant_first');
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.assistantService.getAssistantInfo(this.assistantNo).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.populateForm(response.data);
          this.imagePreviewUrl = response.data.imageurl || '';
        } else {
          this.errorMessage = this.translate.translate('assistant.editor.error_load_assistant_info');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = this.translate.translate('assistant.editor.error_load_assistant');
        this.isLoading = false;
        console.error(this.translate.translate('assistant.editor.console_error_load_info'), error);
      }
    });
  }

  /**
   * 填充表單資料
   */
  private populateForm(assistantData: Assistant): void {
    this.assistantForm.patchValue({
      title: assistantData.title || '',
      content: assistantData.content || '',
      imageurl: assistantData.imageurl || '',
      assistant_text: assistantData.assistant_text || '',
      assistant_bg_color: assistantData.assistant_bg_color || '#f5820d',
      assistant_font_color: assistantData.assistant_font_color || '#ffffff',
      memo: assistantData.memo || ''
    });
  }

  /**
   * 檔案選擇處理
   */
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.handleFileSelection(file);
    }
  }

  /**
   * 拖拉上傳處理
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  /**
   * 處理檔案選擇
   */
  private handleFileSelection(file: File): void {
    // 驗證檔案類型
    if (!file.type.startsWith('image/')) {
      this.errorMessage = this.translate.translate('assistant.editor.error_select_image_file');
      return;
    }

    // 驗證檔案大小 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.errorMessage = this.translate.translate('assistant.editor.error_file_size_exceed');
      return;
    }

    this.selectedFile = file;
    this.uploadFile();
  }

  /**
   * 上傳檔案
   */
  private uploadFile(): void {
    if (!this.selectedFile) return;

    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';

    this.fileUploadService.uploadFile(this.selectedFile).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.uploadProgress = event.progress;
        } else if (event.type === 'success') {
          this.handleUploadSuccess(event.response);
        }
      },
      error: (error) => {
        this.errorMessage = this.translate.translate('assistant.editor.error_upload_failed');
        this.isUploading = false;
        this.uploadProgress = 0;
        console.error(this.translate.translate('assistant.editor.console_error_upload'), error);
      }
    });
  }

  /**
   * 處理上傳成功
   */
  private handleUploadSuccess(response: any): void {
    this.isUploading = false;
    this.uploadProgress = 0;

    if (response && response.data && response.data.viewUrl) {
      const imageUrl = response.data.viewUrl;
      this.assistantForm.patchValue({ imageurl: imageUrl });
      this.imagePreviewUrl = imageUrl;
    } else {
      this.errorMessage = this.translate.translate('assistant.editor.error_upload_no_url');
    }
  }

  /**
   * 移除圖片
   */
  removeImage(): void {
    this.selectedFile = null;
    this.imagePreviewUrl = '';
    this.assistantForm.patchValue({ imageurl: '' });
  }

  /**
   * 防止拖拉事件的預設行為
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
  }

  /**
   * 提交表單
   */
  onSubmit(): void {
    if (!this.assistantNo) {
      this.errorMessage = this.translate.translate('assistant.editor.error_select_assistant_first');
      return;
    }

    if (this.assistantForm.valid) {
      this.saveAssistantInfo();
    } else {
      this.markFormGroupTouched();
      this.errorMessage = this.translate.translate('assistant.editor.error_fill_required_fields');
    }
  }

  /**
   * 儲存助手資訊
   */
  private saveAssistantInfo(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const formData = this.assistantForm.value;

    this.assistantService.updateAssistantInfo(this.assistantNo, formData).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = response.message || this.translate.translate('assistant.editor.success_update');
          // 更新表單以反映最新資料
          if (response.data) {
            this.populateForm(response.data);
          }
        } else {
          this.errorMessage = response.message || this.translate.translate('assistant.editor.error_update_failed');
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = this.translate.translate('assistant.editor.error_update');
        this.isLoading = false;
        console.error(this.translate.translate('assistant.editor.console_error_update'), error);
      }
    });
  }

  /**
   * 標記表單控件為已觸碰
   */
  private markFormGroupTouched(): void {
    Object.keys(this.assistantForm.controls).forEach(key => {
      const control = this.assistantForm.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  /**
   * 取得欄位錯誤訊息
   */
  getFieldError(fieldName: string): string {
    const control = this.assistantForm.get(fieldName);
    if (control && control.errors && control.touched) {
      if (control.errors['required']) {
        return this.translate.translate('assistant.editor.field_required', { fieldName: this.getFieldLabel(fieldName) });
      }
      if (control.errors['maxlength']) {
        return this.translate.translate('assistant.editor.field_maxlength', {
          fieldName: this.getFieldLabel(fieldName),
          maxLength: control.errors['maxlength'].requiredLength
        });
      }
    }
    return '';
  }

  /**
   * 取得欄位標籤
   */
  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      title: this.translate.translate('assistant.editor.title_label'),
      content: this.translate.translate('assistant.editor.content_label'),
      imageurl: this.translate.translate('assistant.editor.image_url'),
      assistant_text: this.translate.translate('assistant.editor.display_text'),
      assistant_bg_color: this.translate.translate('assistant.editor.background_color'),
      assistant_font_color: this.translate.translate('assistant.editor.text_color'),
      memo: this.translate.translate('assistant.editor.memo_label')
    };
    return labels[fieldName] || fieldName;
  }

  /**
   * 重置表單
   */
  resetForm(): void {
    this.assistantForm.reset();
    this.imagePreviewUrl = '';
    this.selectedFile = null;
    this.errorMessage = '';
    this.successMessage = '';

    // 如果有選擇助手，重新載入助手資訊
    if (this.assistantNo) {
      this.loadAssistantInfo();
    }
  }

  /**
   * 選擇預設背景色
   */
  selectPresetBgColor(color: string): void {
    this.assistantForm.patchValue({ assistant_bg_color: color });
  }

  /**
   * 選擇預設文字色
   */
  selectPresetFontColor(color: string): void {
    this.assistantForm.patchValue({ assistant_font_color: color });
  }

  /**
   * 驗證並格式化背景色碼輸入
   */
  onBgColorCodeInput(event: any): void {
    this.onColorCodeInputHelper(event, 'assistant_bg_color');
  }

  /**
   * 驗證並格式化文字色碼輸入
   */
  onFontColorCodeInput(event: any): void {
    this.onColorCodeInputHelper(event, 'assistant_font_color');
  }

  /**
   * 驗證並格式化色碼輸入 (共用邏輯)
   */
  private onColorCodeInputHelper(event: any, fieldName: string): void {
    let value = event.target.value;

    // 移除所有非十六進制字符，但保留 #
    value = value.replace(/[^#0-9A-Fa-f]/g, '');

    // 確保以 # 開頭
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }

    // 限制長度為 7 個字符 (#RRGGBB)
    if (value.length > 7) {
      value = value.substring(0, 7);
    }

    // 轉換為大寫
    value = value.toUpperCase();

    // 更新輸入框顯示（不觸發表單驗證）
    event.target.value = value;

    // 如果是完整的色碼格式，更新表單值
    if (value.length === 7) {
      const isValidColor = /^#[0-9A-Fa-f]{6}$/.test(value);
      if (isValidColor) {
        this.assistantForm.patchValue({ [fieldName]: value }, { emitEvent: false });
      }
    }
  }

  /**
   * 驗證並格式化色碼輸入 (保留舊方法以向後相容)
   */
  onColorCodeInput(event: any): void {
    this.onColorCodeInputHelper(event, 'assistant_bg_color');
  }

  /**
   * 選擇預設顏色 (保留舊方法以向後相容)
   */
  selectPresetColor(color: string): void {
    this.selectPresetBgColor(color);
  }
}
